/**
 * Choix de consentement aux traceurs (DECISIONS §11, A-41).
 *
 * Le choix est enregistré dans le stockage local du navigateur, sous la clé
 * `elemora.consentement` : c'est la **preuve du choix**, elle est donc elle-même essentielle et
 * ne demande aucun consentement. Aucun identifiant de mesure n'est écrit tant que le joueur n'a
 * pas accepté la mesure d'audience (voir `mesure-audience.ts`).
 *
 * Ce module est volontairement sans React : la bannière, la page `/cookies` et les tests lisent le
 * même état par `useChoixConsentement`, adossé à `useSyncExternalStore`. Un seul état, une seule
 * écriture : deux composants montés en même temps ne peuvent pas diverger.
 *
 * Expiration : le choix est redemandé au bout de six mois (durée annoncée dans la politique de
 * cookies). Un choix expiré n'est pas supprimé du stockage — il est ignoré, et le prochain choix
 * l'écrase. Cela évite une écriture au chargement de l'application.
 */
import { useSyncExternalStore } from 'react';

import { COOKIE_LOCALE } from '@elemora/shared/constants';

import { CLE_RUN_EN_COURS } from '../jeu/marqueur-run';

/** Clé de stockage local du choix (essentielle : preuve du consentement). */
export const CLE_CONSENTEMENT = 'elemora.consentement';

/** Version du schéma de choix : la faire évoluer redemande le consentement. */
export const VERSION_CONSENTEMENT = 1;

/** Durée de validité du choix, en jours (six mois, valeur annoncée dans la politique). */
export const DUREE_CONSENTEMENT_JOURS = 182;

/** Origine du choix : bouton « tout accepter », « refuser » ou panneau « personnaliser ». */
export type OrigineChoix = 'tout-accepter' | 'refuser' | 'personnaliser';

/** Choix enregistré. Aucun identifiant, aucune donnée personnelle : trois faits et une date. */
export interface ChoixConsentement {
  readonly version: number;
  /** Horodatage ISO du choix. */
  readonly date: string;
  /** `true` si la mesure d'audience facultative est acceptée. */
  readonly mesureAudience: boolean;
  readonly origine: OrigineChoix;
}

/**
 * Traceurs et stockages **essentiels** : session, langue, choix de consentement, partie en cours.
 *
 * Cette liste est la référence du contrôle automatisé (`consentement.spec.tsx`) et du diagnostic
 * `identifiantsNonEssentiels()` : tout ce qui n'y figure pas est facultatif et ne doit apparaître
 * qu'après un consentement explicite.
 */
export const COOKIES_ESSENTIELS: readonly string[] = [
  // Cookie de rafraîchissement posé par le serveur (HttpOnly, session).
  'elemora_rt',
  // Langue choisie : préférence demandée explicitement par le joueur (DECISIONS §9.3).
  COOKIE_LOCALE,
];

/** Clés de stockage local indispensables au service. */
export const STOCKAGES_ESSENTIELS: readonly string[] = [
  CLE_CONSENTEMENT,
  // Marqueur de run en cours (sessionStorage, identifiant de run uniquement). L'écran d'accueil le
  // purge à l'entrée (carte U-02) ; la clé est définie dans `jeu/marqueur-run.ts`, source unique.
  CLE_RUN_EN_COURS,
];

/** Identifiants facultatifs : écrits seulement si la mesure d'audience est acceptée. */
export const COOKIES_FACULTATIFS: readonly string[] = ['elemora_ma'];
export const STOCKAGES_FACULTATIFS: readonly string[] = ['elemora.mesure-audience'];

/** Abonnés au changement de choix (bannière, page cookies, mesure d'audience). */
const abonnes = new Set<() => void>();

/**
 * Cache de la dernière valeur lue : `useSyncExternalStore` exige un instantané **stable** (même
 * référence tant que rien ne change), sinon React boucle.
 */
let cache: ChoixConsentement | null | undefined;

/** Vrai si la valeur analysée respecte le schéma du choix. */
function estChoixValide(valeur: unknown): valeur is ChoixConsentement {
  if (valeur === null || typeof valeur !== 'object') return false;
  const candidat = valeur as Partial<ChoixConsentement>;
  return (
    candidat.version === VERSION_CONSENTEMENT &&
    typeof candidat.date === 'string' &&
    !Number.isNaN(Date.parse(candidat.date)) &&
    typeof candidat.mesureAudience === 'boolean' &&
    (candidat.origine === 'tout-accepter' ||
      candidat.origine === 'refuser' ||
      candidat.origine === 'personnaliser')
  );
}

/** Vrai si le choix dépasse sa durée de validité. */
function estExpire(choix: ChoixConsentement): boolean {
  const date = Date.parse(choix.date);
  return Date.now() - date > DUREE_CONSENTEMENT_JOURS * 24 * 60 * 60 * 1000;
}

/** Lit le choix enregistré ; `null` s'il est absent, illisible, d'une autre version ou expiré. */
export function lireChoixConsentement(): ChoixConsentement | null {
  if (cache !== undefined) return cache;
  try {
    const brut = window.localStorage.getItem(CLE_CONSENTEMENT);
    if (brut === null) {
      cache = null;
      return cache;
    }
    const analyse: unknown = JSON.parse(brut);
    cache = estChoixValide(analyse) && !estExpire(analyse) ? analyse : null;
  } catch {
    // Stockage indisponible (navigation privée restrictive) : aucun choix connu, la bannière
    // s'affiche et le choix ne sera pas mémorisé. Le jeu reste jouable.
    cache = null;
  }
  return cache;
}

/** Prévient les composants abonnés qu'un nouveau choix est disponible. */
function notifier(): void {
  for (const abonne of abonnes) abonne();
}

/**
 * Enregistre un choix et le rend actif immédiatement.
 *
 * Le stockage peut refuser l'écriture (quota, navigation privée) : le choix reste alors valable
 * pour la session en cours, sans être mémorisé — jamais d'erreur visible pour le joueur.
 */
export function enregistrerChoixConsentement(
  mesureAudience: boolean,
  origine: OrigineChoix,
): ChoixConsentement {
  const choix: ChoixConsentement = {
    version: VERSION_CONSENTEMENT,
    date: new Date().toISOString(),
    mesureAudience,
    origine,
  };
  cache = choix;
  try {
    window.localStorage.setItem(CLE_CONSENTEMENT, JSON.stringify(choix));
  } catch {
    // Choix non mémorisé : la bannière reviendra au prochain chargement, rien d'autre.
  }
  notifier();
  return choix;
}

/** Abonnement `useSyncExternalStore`. */
function abonner(abonne: () => void): () => void {
  abonnes.add(abonne);
  return () => {
    abonnes.delete(abonne);
  };
}

/** Instantané stable du choix courant. */
function instantane(): ChoixConsentement | null {
  return lireChoixConsentement();
}

/** Choix courant, réactif : bannière et page `/cookies` partagent ce hook. */
export function useChoixConsentement(): ChoixConsentement | null {
  return useSyncExternalStore(abonner, instantane, instantane);
}

/**
 * Liste les identifiants **non essentiels** présents dans le navigateur.
 *
 * Sert de garde-fou exécutable : le test de consentement exige une liste vide avant tout choix et
 * après un refus. Un identifiant oublié dans une catégorie facultative fait échouer ce contrôle au
 * lieu de passer inaperçu.
 */
export function identifiantsNonEssentiels(): string[] {
  const trouves: string[] = [];
  if (typeof document !== 'undefined') {
    for (const morceau of document.cookie.split(';')) {
      const nom = morceau.split('=')[0]?.trim();
      if (nom === undefined || nom === '') continue;
      // Fermé par défaut : un cookie non déclaré essentiel est compté comme facultatif. Un
      // identifiant ajouté plus tard sans être classé fait donc échouer le contrôle au lieu de
      // passer inaperçu — c'est le sens de « aucun cookie non essentiel avant consentement ».
      if (COOKIES_ESSENTIELS.includes(nom)) continue;
      const prefixe = COOKIES_FACULTATIFS.includes(nom) ? 'cookie' : 'cookie-non-declare';
      trouves.push(`${prefixe}:${nom}`);
    }
  }
  try {
    for (const cle of Object.keys(window.localStorage)) {
      if (STOCKAGES_ESSENTIELS.includes(cle)) continue;
      const prefixe = STOCKAGES_FACULTATIFS.includes(cle) ? 'stockage' : 'stockage-non-declare';
      trouves.push(`${prefixe}:${cle}`);
    }
  } catch {
    // Stockage inaccessible : rien à inventorier.
  }
  return trouves;
}

/** Réinitialise le cache mémoire (tests uniquement : le stockage est vidé par `setup.ts`). */
export function reinitialiserConsentement(): void {
  cache = undefined;
  notifier();
}
